import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VoiceInput } from '../components/features/VoiceInput';
import { ShareModal } from '../components/features/ShareModal';
import { parseTranscription } from '../utils/text-parser';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { generateVCardData, downloadVCard, type VCardContact } from '../utils/vcard';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { FiMessageSquare, FiShare, FiRotateCw } from 'react-icons/fi';
import styles from './Home.module.css';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentProfile, refreshProfile, profiles, switchProfile } = useAuth();
  const { tier, showUpgradeModal } = useSubscription();
  const [_transcribedData, setTranscribedData] = useState<Partial<VCardContact>>({});
  const [error, setError] = useState<string | null>(null);
  const [lastContact, setLastContact] = useState<VCardContact | null>(null);
  const [_isListening, setIsListening] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);

  const isEnhancedRecognition = tier !== 'FREE';

  useEffect(() => {
    // Show welcome message if user just logged in
    if (location.state?.justLoggedIn && user) {
      const firstName = user.name?.split(' ')[0] || 'User';
      const isNewUser = location.state?.isNewUser;
      setWelcomeMessage(isNewUser ? `Welcome ${firstName}!` : `Welcome back, ${firstName}!`);
      // Clear welcome message after 3 seconds
      setTimeout(() => setWelcomeMessage(null), 3000);
      // Clear the state so message doesn't show on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, user, navigate]);

  // Ensure profile data is fresh and shortId is available
  useEffect(() => {
    const initializeProfile = async () => {
      if (!user?._id) return;

      try {
        // If no current profile but we have profiles, switch to the most recent one
        if (!currentProfile && profiles && profiles.length > 0) {
          const sortedProfiles = [...profiles].sort((a, b) => 
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );
          
          if (sortedProfiles[0]?._id) {
            await switchProfile(sortedProfiles[0]._id);
          }
        }

        // Always refresh current profile data to ensure shortId is available
        if (currentProfile) {
          const refreshedProfile = await refreshProfile();
          if (!refreshedProfile || !refreshedProfile.shortId) {
            console.error('Profile missing shortId after refresh:', refreshedProfile);
            setError('Profile data is not ready. Please try again in a moment.');
          }
        }
      } catch (error) {
        console.error('Failed to initialize profile:', error);
        setError('Failed to load profile data. Please refresh the page.');
      }
    };

    initializeProfile();
  }, [user?._id, currentProfile?._id, profiles]);

  // Reset error when profile changes
  useEffect(() => {
    if (currentProfile?.shortId) {
      setError(null);
    }
  }, [currentProfile?.shortId]);

  const handleTranscription = (text: string) => {
    const parsedData = parseTranscription(text);
    
    // Only update if we found a name and at least one contact method
    if (parsedData.name && (parsedData.phone || parsedData.email)) {
      const contact: VCardContact = {
        name: parsedData.name,
        phone: parsedData.phone || '',
        email: parsedData.email,
        notes: currentProfile?.friendlyName ? `Added with Say profile: ${currentProfile.friendlyName}` : undefined,
        categories: currentProfile?.friendlyName ? [currentProfile.friendlyName] : undefined
      };
      setTranscribedData(contact);
      setLastContact(contact);
      
      // Save their contact first - this triggers immediate vCard download
      downloadVCard(contact, tier);
    } else {
      setError('Could not detect name and at least one contact method. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleVoiceStart = () => {
    setIsListening(true);
    setError(null);
  };

  const handleVoiceEnd = () => {
    setIsListening(false);
  };

  const handleVoiceButtonClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
  };

  const handleSendMyCard = async () => {
    if (!user?._id || !lastContact?.phone || !user?.name || !currentProfile?.shortId) {
      setError('Missing required information. Please try again.');
      return;
    }
    
    try {
      // Construct SMS message using current profile's shortId
      const firstName = currentProfile.name?.split(' ')[0] || user?.name?.split(' ')[0] || 'User';
      const message = `Hi ${lastContact.name.split(' ')[0]}\n\nClick this link to save my contact details,\nhttps://say.cards/mysay/${currentProfile.shortId}\nKind regards\n${firstName}\n\nShare your details.\nJoin for FREE now https://say.cards`;
      
      // Encode message for SMS URI
      const encodedMessage = encodeURIComponent(message);
      
      // Open default SMS app with pre-populated message
      window.open(`sms:${lastContact.phone}?body=${encodedMessage}`, '_blank');
    } catch (error) {
      console.error('Failed to generate vCard link:', error);
      setError('Failed to generate contact sharing link. Please try again.');
    }
  };

  const handleShareMyCard = async () => {
    if (!lastContact || !currentProfile?.name || !currentProfile) return;
    
    // Create vCard for the user's contact
    const myContact: VCardContact = {
      name: currentProfile.name || 'User',
      phone: currentProfile.phone || '',
      email: user?.email
    };
    
    const vCardData = generateVCardData(myContact);
    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const file = new File([blob], `${myContact.name}.vcf`, { type: 'text/vcard' });
    
    // Try to use the native share API first
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `Contact details for ${myContact.name}`,
          text: `Contact details for ${myContact.name}\n\nGet your FREE SayCard now! https://say.cards`
        });
      } catch (error) {
        console.log('Share failed:', error);
        // If user cancelled, don't show error
        if ((error as any)?.name === 'AbortError') return;
        
        // Platform specific fallback
        if (/iPad|iPhone|iPod|Android/.test(navigator.userAgent)) {
          // On mobile, create SMS link as fallback
          const recipientPhone = lastContact.phone.replace(/\D/g, '');
          const smsUrl = `sms:${recipientPhone}`;
          window.location.href = smsUrl;
        } else {
          // On desktop, show a helpful message
          setError('Unable to share. Try sending your contact details another way.');
          setTimeout(() => setError(null), 3000);
        }
      }
    } else {
      // If share API not supported, provide platform-specific fallback
      if (/iPad|iPhone|iPod|Android/.test(navigator.userAgent)) {
        const recipientPhone = lastContact.phone.replace(/\D/g, '');
        const smsUrl = `sms:${recipientPhone}`;
        window.location.href = smsUrl;
      } else {
        setError('Sharing not available. Try sending your contact details another way.');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const handleClear = () => {
    setLastContact(null);
    setTranscribedData({});
    setError(null);
  };

  return (
    <div className={styles.container}>
      {welcomeMessage && (
        <div className={styles.welcomeMessage}>
          {welcomeMessage}
        </div>
      )}

      <div className="max-w-md w-full space-y-8 bg-white p-6 rounded-xl shadow-lg">
        <div className={styles.header}>
          <h1 className={styles.title}>Voice to Contacts</h1>
          {user && (
            <button
              onClick={() => setIsShareModalOpen(true)}
              className={styles.shareButton}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
              Share This Profile
            </button>
          )}
        </div>

        <p className={styles.description}>
          Speak the contact's name and phone number or email clearly. You can also include their
          email address. A contact card will be created automatically. You can optionally share your selected profile details back.
        </p>

        {user ? (
          <VoiceInput
            onTranscription={handleTranscription}
            onStart={handleVoiceStart}
            onEnd={handleVoiceEnd}
          />
        ) : (
          <button
            onClick={handleVoiceButtonClick}
            className="
              px-6 py-3 rounded-full font-medium text-white
              transition-all duration-200 transform hover:scale-105
              flex items-center space-x-2
              bg-blue-500 hover:bg-blue-600
            "
          >
            <span className="w-3 h-3 rounded-full bg-white"></span>
            <span>Say your name and number</span>
          </button>
        )}

        {user && tier === 'FREE' && (
          <div className="mt-4 flex items-center justify-center text-sm text-gray-600">
            <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.477.859h4z" />
            </svg>
            <span>Enjoy better audio conversion</span>
            <button 
              onClick={() => showUpgradeModal()}
              className="ml-2 text-blue-500 hover:text-blue-600 font-medium"
            >
              Upgrade now →
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {lastContact && (
          <div className={styles.capturedSection}>
            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-col gap-2 w-full">
                <button
                  onClick={handleSendMyCard}
                  className={styles.sendButton}
                  disabled={!user?._id}
                >
                  <FiMessageSquare className="w-5 h-5" />
                  <span>Text my details back to {lastContact.name.split(' ')[0]}</span>
                </button>

                <button
                  onClick={handleShareMyCard}
                  className={styles.sendButton}
                  disabled={!user?.name}
                >
                  <FiShare className="w-5 h-5" />
                  <span>Share with {lastContact.name.split(' ')[0]} by app</span>
                </button>
              </div>

              <button
                onClick={handleClear}
                className={styles.clearButton}
                title="Clear Results"
              >
                <FiRotateCw className="w-5 h-5" />
                <span>Clear Results</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />

      {/* Enhanced Recognition Badge - Moved to bottom */}
      {isEnhancedRecognition && (
        <div className="flex justify-end mt-4 mb-2">
          <div className="inline-flex items-center bg-gradient-to-r from-purple-500 to-blue-500 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-lg">
            <SparklesIcon className="h-4 w-4 mr-1.5 animate-pulse" />
            Enhanced Recognition
          </div>
        </div>
      )}
    </div>
  );
};
